import { addError, addSuccess } from '@/store/reducers/ToastStore';
import { EventType } from '@/models/Interaction';
import {
  FeedbackButtons,
  FeedbackButtonsTheme,
} from '@/components/chat/FeedbackButtons';
import { getChatAPI } from '@/api/ChatApi';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { getThreadById } from '@/store/reducers/ChatStore';
import { MessageId } from '@/models/Message';
import { PAPER_FINDER_FEEDBACK_PREFIX } from '@/analytics/props/feedback';
import { Reaction } from '@/models/Reaction';
import { ThreadId } from '@/models/Thread';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { WidgetId } from '@/models/PanelWidgets';

import { useState } from 'react';

type PaperFinderFeedbackButtonsProps = {
  messageId: MessageId;
  threadId: ThreadId;
  widgetId: WidgetId;
};

export const PaperFinderFeedbackButtons = ({
  messageId,
  threadId,
  widgetId,
}: PaperFinderFeedbackButtonsProps) => {
  const thread = useAppSelector((state) => getThreadById(state, threadId));
  const dispatch = useAppDispatch();
  const chatApi = getChatAPI();
  const [reaction, setReaction] = useState<Reaction | null>(null);

  const onSubmitFeedback = async (feedback: string) => {
    if (!thread) {
      getLayoverLogger().logError(
        `threadId not found in ChatStore [threadId=${threadId}]`,
      );
      return;
    }

    try {
      await chatApi.trackEvent({
        type: EventType.WIDGET_PAPER_FINDER_TEXT_FEEDBACK,
        threadId: threadId,
        text: feedback,
        data: { message_id: messageId, widget_id: widgetId },
      });
      dispatch(addSuccess({ text: 'Thanks for your feedback!' }));
    } catch (e: any) {
      getLayoverLogger().logError('Error saving section feedback:', e);
      dispatch(
        addError({ text: 'Error submitting feedback. Please try again.' }),
      );
    }
  };

  const onUpdateReactionState = async (newReaction: Reaction | null) => {
    if (!thread) {
      getLayoverLogger().logError(
        `threadId not found in ChatStore [threadId=${threadId}]`,
      );
      return;
    }
    const previousReaction = reaction;

    try {
      setReaction(newReaction);
      await chatApi.trackEvent({
        type: EventType.WIDGET_PAPER_FINDER_REACTION,
        threadId: threadId,
        text: newReaction?.valueOf(),
        data: { message_id: messageId, widget_id: widgetId },
      });
    } catch (e: any) {
      setReaction(previousReaction);
      getLayoverLogger().logError(
        'Failed to update paperfinder feedback state:',
        e,
      );
      dispatch(addError({ text: 'Error saving feedback. Please try again.' }));
    }
  };

  return (
    <FeedbackButtons
      viewerReaction={reaction}
      onUpdateReactionState={onUpdateReactionState}
      onSubmitFeedback={onSubmitFeedback}
      theme={FeedbackButtonsTheme.DARK}
      heapPrefix={PAPER_FINDER_FEEDBACK_PREFIX}
      heapProps={{ messageId, threadId, widgetId }}
    />
  );
};
